// src/utils/http.ts
import { redirectToLogin } from "./auth";
import { isRequiredPasswordChangeLogoutInProgress } from "./passwordChangeLogout";

/**
 * @deprecated JWT tokens are now in HTTP-only cookies, not Authorization headers
 * This function is kept for backward compatibility but returns empty object
 */
function authHeaders(): Record<string, string> {
    // Tokens are now in HTTP-only cookies, no need for Authorization header
    return {};
}

async function handleResponse<T>(res: Response, url: string): Promise<T> {
    const text = await res.text();
    let body: any = null;
    try { body = text ? JSON.parse(text) : null; } catch { /* non-json */ }

    if (!res.ok) {
        // ⛔ Auto-redirect on auth problems
        if (res.status === 401 || res.status === 403) {
            if (!isRequiredPasswordChangeLogoutInProgress()) {
                redirectToLogin(true);
            }
            // throw to stop any further handling
            const msg = body?.detail || body?.message || `${res.status} ${res.statusText}`;
            const err = new Error(msg) as any;
            err.status = res.status; err.body = body ?? text; err.url = url;
            throw err;
        }
        const msg = body?.detail || body?.message || `${res.status} ${res.statusText}`;
        const err = new Error(msg) as any;
        err.status = res.status; err.body = body ?? text; err.url = url;
        throw err;
    }
    return body as T;
}

export async function jsonGet<T>(url: string, useCookies = true): Promise<T> {
    const res = await fetch(url, {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include", // Always include cookies for authentication
    });
    return handleResponse<T>(res, url);
}

export async function jsonSend<T>(
    url: string,
    method: "POST" | "PUT" | "PATCH" | "DELETE",
    body?: any,
    useCookies = true
): Promise<T> {
    const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "include", // Always include cookies for authentication
        body: JSON.stringify(body ?? {}),
    });
    return handleResponse<T>(res, url);
}

// src/utils/http.ts
export async function uploadFile(url: string, file: File, useCookies = true) {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(url, {
        method: "POST",
        body: fd,
        credentials: "include" // Always include cookies for authentication
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
}